import vtkImageData from '@kitware/vtk.js/Common/DataModel/ImageData';
import { FILE_EXT_TO_MIME } from '@/src/io/mimeTypes';
import type { DataSource } from '@/src/io/import/dataSource';
import { nextTick, ref } from 'vue';

export type NiftiImportIntent = 'volume' | 'segmentation';

export interface PendingSegmentationImport {
  dataSource: DataSource;
  fileName: string;
  imageData: vtkImageData;
}

/** MIME assigned to NIfTI files by `updateFileMimeType` / `getMimeTypeFromFilename`. */
export const NIFTI_MIME = FILE_EXT_TO_MIME.nii;

const NIFTI_EXTENSIONS = ['.nii', '.nii.gz', '.nia', '.hdr'];

export function isNiftiFileName(fileName: string) {
  const lower = fileName.toLowerCase();
  return NIFTI_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

/**
 * Detect NIfTI imports. `dataSource.fileType` is a MIME string (e.g.
 * `application/vnd.unknown.nifti-1`), not a bare extension like `nii`.
 */
export function isNiftiDataSource(dataSource: DataSource) {
  if (dataSource.type !== 'file') return false;
  if (dataSource.fileType === NIFTI_MIME) return true;
  return isNiftiFileName(dataSource.file.name);
}

/** @deprecated Use {@link isNiftiDataSource} — fileType is MIME, not extension. */
export function isNiftiFileType(fileType: string) {
  return fileType === NIFTI_MIME;
}

export const niftiImportDialogOpen = ref(false);
export const niftiImportFileName = ref('');
export const niftiImportLoadingMessage = ref('');

/**
 * Whether segmentation import work is currently running. Drives the dialog's
 * `persistent` state and blocks user dismissal. Cleared as soon as work
 * finishes (see {@link finishNiftiImportDialog}).
 */
export const niftiImportInFlight = ref(false);

/**
 * Whether the dialog body shows the progress view (vs. the volume/segmentation
 * prompt). Intentionally decoupled from {@link niftiImportInFlight}: it stays
 * `true` through the dialog's close animation so the prompt buttons don't flash
 * back in for a frame as the dialog fades out. Reset on the next open.
 */
export const niftiImportShowProgress = ref(false);

const pendingSegmentations: PendingSegmentationImport[] = [];

let resolveIntent: ((intent: NiftiImportIntent) => void) | null = null;
let rejectIntent: ((reason: Error) => void) | null = null;
let intentResolved = false;

/**
 * Waits for the browser to actually paint a frame.
 *
 * `nextTick()` only flushes Vue's DOM mutations as a microtask; it resolves
 * BEFORE the browser renders. Since the segmentation import does heavy
 * synchronous work (scanning every voxel) right after, that work would block
 * the main thread before the loading UI is ever painted. A double rAF resolves
 * after the first frame has been committed, guaranteeing the dialog is visible.
 */
export function nextPaint(): Promise<void> {
  if (typeof requestAnimationFrame !== 'function') {
    return nextTick();
  }
  return new Promise<void>((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => resolve());
    });
  });
}

export async function requestNiftiImportIntent(
  fileName: string
): Promise<NiftiImportIntent> {
  niftiImportFileName.value = fileName;
  niftiImportInFlight.value = false;
  niftiImportShowProgress.value = false;
  niftiImportLoadingMessage.value = '';
  niftiImportDialogOpen.value = true;
  intentResolved = false;
  // Let the dialog mount before the import pipeline blocks on the answer.
  await nextTick();
  return new Promise((resolve, reject) => {
    resolveIntent = resolve;
    rejectIntent = reject;
  });
}

export async function confirmNiftiImportIntent(intent: NiftiImportIntent) {
  intentResolved = true;
  if (intent === 'segmentation') {
    niftiImportShowProgress.value = true;
    niftiImportInFlight.value = true;
    niftiImportLoadingMessage.value = 'Importing segmentation...';
  } else {
    // Volume: nothing else to process, close immediately. The content stays on
    // the prompt view, which is what's already shown, so no flash.
    niftiImportDialogOpen.value = false;
  }
  // Paint the loading state before the import pipeline resumes heavy work.
  // Must be a real frame, not just nextTick, or the synchronous segment scan
  // blocks the main thread before the progress UI is ever rendered.
  await nextPaint();
  resolveIntent?.(intent);
  resolveIntent = null;
  rejectIntent = null;
}

export function setNiftiImportProgress(message: string) {
  if (niftiImportInFlight.value) {
    niftiImportLoadingMessage.value = message;
  }
}

export function finishNiftiImportDialog() {
  // Clear the in-flight guard and close, but leave `showProgress` set so the
  // close animation keeps rendering the progress view instead of briefly
  // flashing the prompt buttons. The next open resets it.
  niftiImportInFlight.value = false;
  niftiImportDialogOpen.value = false;
}

export function cancelNiftiImportIntent() {
  if (niftiImportInFlight.value) return;
  if (intentResolved) {
    intentResolved = false;
    return;
  }
  niftiImportDialogOpen.value = false;
  rejectIntent?.(new Error('NIfTI import cancelled'));
  resolveIntent = null;
  rejectIntent = null;
}

export function queuePendingSegmentation(entry: PendingSegmentationImport) {
  pendingSegmentations.push(entry);
}

export function drainPendingSegmentations() {
  const pending = [...pendingSegmentations];
  pendingSegmentations.length = 0;
  return pending;
}

export function clearPendingSegmentations() {
  pendingSegmentations.length = 0;
}
